if (!customElements.get('shoppable-images-multi-image-option')) {
  customElements.define(
    'shoppable-images-multi-image-option',
    class ShoppableImagesMultiImageOption extends HTMLElement {
      constructor() {
        super();
        this.cartElement = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.mediaQuery = window.matchMedia('(min-width: 750px)');
        this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.hoverCloseTimeout = null;

        this.attachEventListeners();
        this.setupResizeObserver();

        if (!(window.Shopify && window.Shopify.designMode)) {
          const firstGroup = this.querySelector('.hotspot-block-anchor')?.dataset.blockId;
          if (firstGroup) this.openGroup(firstGroup);
        }
      }

      getDots() {
        return Array.from(this.querySelectorAll('.hotspot-dot'));
      }

      getPopups() {
        return Array.from(this.querySelectorAll('.hotspot-popup'));
      }

      attachEventListeners() {
        // Delegated listener: works even if popup/button markup is re-rendered later,
        // and avoids any risk of per-element listeners silently failing to attach.
        this.addEventListener('click', (event) => {
          const dot = event.target.closest('.hotspot-dot');
          if (dot && this.contains(dot)) {
            this.onDotClick({ currentTarget: dot, stopPropagation: () => event.stopPropagation() });
            return;
          }

          const addButton = event.target.closest('.hotspot-popup__add');
          if (addButton && this.contains(addButton)) {
            this.onAddSingle({ currentTarget: addButton, stopPropagation: () => event.stopPropagation() });
            return;
          }

          const ctaButton = event.target.closest('.shoppable-images-multi-image-option__cta');
          if (ctaButton && this.contains(ctaButton)) {
            this.onAddAll({ currentTarget: ctaButton, stopPropagation: () => event.stopPropagation() });
            return;
          }

          const optionButton = event.target.closest('.shoppable-images-multi-image-option__option');
          if (optionButton && this.contains(optionButton)) {
            this.selectOption(optionButton.dataset.optionIndex);
          }
        });

        this.getDots().forEach((dot) => {
          dot.style.pointerEvents = 'auto';
          dot.style.cursor = 'pointer';
        });

        // Hover-to-open on devices with a real mouse (desktop). Delegated so it
        // also covers dots/popups added or re-rendered later.
        this.addEventListener('mouseover', (event) => {
          if (!this.hoverQuery.matches) return;
          const dot = event.target.closest('.hotspot-dot');
          const popup = event.target.closest('.hotspot-popup');
          if (!dot && !popup) return;
          this.cancelHoverClose();
          if (dot && this.contains(dot)) this.openPopup(dot.dataset.hotspotId);
        });

        this.addEventListener('mouseout', (event) => {
          if (!this.hoverQuery.matches) return;
          const leavingDotOrPopup = event.target.closest('.hotspot-dot, .hotspot-popup');
          if (!leavingDotOrPopup) return;
          const enteringDotOrPopup = event.relatedTarget && event.relatedTarget.closest
            ? event.relatedTarget.closest('.hotspot-dot, .hotspot-popup')
            : null;
          if (enteringDotOrPopup && this.contains(enteringDotOrPopup)) return;
          this.scheduleHoverClose();
        });

        document.addEventListener('click', this.onDocumentClick.bind(this));

        this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
        this.addEventListener('shopify:block:deselect', this.onBlockDeselect.bind(this));
      }

      scheduleHoverClose() {
        this.cancelHoverClose();
        this.hoverCloseTimeout = setTimeout(() => this.closeAllPopups(), 150);
      }

      cancelHoverClose() {
        if (this.hoverCloseTimeout) {
          clearTimeout(this.hoverCloseTimeout);
          this.hoverCloseTimeout = null;
        }
      }

      setupResizeObserver() {
        this.mediaQuery.addListener(() => {
          this.closeAllPopups();
        });
      }

      isDisplayNone(el) {
        const style = window.getComputedStyle(el);
        return style.display === 'none';
      }

      onBlockSelect(event) {
        const groupId = event.target.dataset.blockId;
        if (!groupId) return;
        this.closeAllPopups();

        if (event.target.dataset.optionSelect === 'true') {
          this.selectOption(event.target.dataset.optionIndex);
          return;
        }

        this.openGroup(groupId);
      }

      onBlockDeselect() {
        this.closeAllPopups();
      }

      selectOption(index) {
        if (index === undefined || index === null) return;

        this.querySelectorAll('.shoppable-images-multi-image-option__option').forEach((button) => {
          button.classList.toggle('active', button.dataset.optionIndex === index);
        });

        this.querySelectorAll('.shoppable-images-multi-image-option__option-media').forEach((media) => {
          media.classList.toggle('active', media.dataset.optionIndex === index);
        });

        this.closeAllPopups();

        const activeButton = this.querySelector(
          `.shoppable-images-multi-image-option__option[data-option-index="${index}"]`
        );
        const cta = this.querySelector('.shoppable-images-multi-image-option__cta');
        if (!activeButton || !cta) return;

        cta.dataset.variantIds = activeButton.dataset.variantIds || '';

        if (cta.dataset.priceOverride !== 'true') {
          const priceEl = cta.querySelector('.shoppable-images-multi-image-option__cta-price');
          if (priceEl) priceEl.textContent = activeButton.dataset.price || '';
        }
      }

      openGroup(groupId) {
        const dots = this.getDots();
        dots
          .filter((dot) => dot.dataset.hotspotGroup === groupId && !this.isDisplayNone(dot))
          .forEach((dot) => this.openPopup(dot.dataset.hotspotId));
      }

      onDotClick(event) {
        event.stopPropagation();
        const id = event.currentTarget.dataset.hotspotId;
        const isActive = event.currentTarget.classList.contains('active');
        this.closeAllPopups();
        if (!isActive) this.openPopup(id);
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.closeAllPopups();
      }

      openPopup(id) {
        this.closeAllPopups();

        const dots = this.getDots();
        const popups = this.getPopups();

        const dot = dots.find((el) => el.dataset.hotspotId === id && !this.isDisplayNone(el));
        const popup = popups.find((el) => el.dataset.hotspotPopupId === id && !this.isDisplayNone(el));

        if (dot) {
          dot.classList.add('active');
          dot.setAttribute('aria-expanded', 'true');
          dot.style.pointerEvents = 'auto';
        }

        if (popup) {
          popup.classList.add('active');
          popup.style.pointerEvents = 'auto';
          popup.style.visibility = 'visible';
          popup.style.opacity = '1';
        }
      }

      closeAllPopups() {
        const dots = this.getDots();
        const popups = this.getPopups();

        dots.forEach((dot) => {
          dot.classList.remove('active');
          dot.setAttribute('aria-expanded', 'false');
        });
        popups.forEach((popup) => {
          popup.classList.remove('active');
          popup.style.visibility = '';
          popup.style.opacity = '';
        });
      }

      getSectionsToAdd() {
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      updateCartDisplay(response) {
        const sections = response && response.sections;
        if (!sections) return;

        const parser = new DOMParser();

        if (sections['cart-drawer']) {
          const currentDrawerInner = document.querySelector('cart-drawer #CartDrawer');
          const newDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newDrawerInner = newDoc.querySelector('#CartDrawer');
          if (currentDrawerInner && newDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }
        }

        if (sections['cart-icon-bubble']) {
          const currentBubble = document.getElementById('cart-icon-bubble');
          const newDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubbleContent = newDoc.querySelector('.shopify-section');
          if (currentBubble && newBubbleContent) {
            currentBubble.innerHTML = newBubbleContent.innerHTML;
          }
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            cartDrawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: response, bubbles: true }));
        document.dispatchEvent(new Event('shopify:cart:updated', { bubbles: true }));
      }

      addToCart(items, button) {
        if (!items || items.length === 0) {
          console.error('No items to add to cart');
          return Promise.reject(new Error('No items to add'));
        }

        if (button) {
          button.classList.add('is-loading');
          button.setAttribute('aria-disabled', 'true');
        }

        const sections = this.getSectionsToAdd();
        const body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });

        return fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            console.log('API Response:', response);

            if (response.status) {
              console.error('❌ Error:', response.message || response.description);
              return Promise.reject(response);
            }

            console.log('✅ Product added successfully');

            this.updateCartDisplay(response);

            return response;
          })
          .catch((error) => {
            console.error('❌ Error adding to cart:', error);
            throw error;
          })
          .finally(() => {
            if (button) {
              button.classList.remove('is-loading');
              button.removeAttribute('aria-disabled');
            }
          });
      }

      onAddSingle(event) {
        if (event.stopPropagation) {
          event.stopPropagation();
        }

        const button = event.currentTarget;
        const variantId = button.dataset.variantId;

        if (!variantId) {
          console.error('❌ No variant ID found on add button');
          return;
        }

        this.addToCart([{ id: variantId, quantity: 1 }], button).then((response) => {
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 1500);

          // Close popup after a short delay
          setTimeout(() => this.closeAllPopups(), 300);
        }).catch((error) => {
          console.error('❌ Error adding to cart:', error);
        });
      }

      onAddAll(event) {
        event.stopPropagation();
        const button = event.currentTarget;
        const variantIds = (button.dataset.variantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        if (!variantIds.length) {
          console.error('No variant IDs found on add all button');
          return;
        }

        const items = variantIds.map((id) => ({ id, quantity: 1 }));
        this.addToCart(items, button);
      }
    }
  );
}
